import { writable } from 'svelte/store';
import { browser } from '$app/environment';

/**
 * Global configuration store for app-wide settings
 */

export interface AppConfig {
	// Debug settings
	debug: {
		enabled: boolean;
		ik: boolean;
		bodyTracking: boolean;
		mediaPipe: boolean;
		mujoco: boolean;
	};

	// MediaPipe settings
	mediaPipe: {
		modelComplexity: 0 | 1 | 2;
		minDetectionConfidence: number;
		minTrackingConfidence: number;
		smoothLandmarks: boolean;
	};

	// G1 Controller settings
	g1Controller: {
		autoInitialize: boolean;
		kp: number;
		kd: number | null;
		debug: boolean;
	};

	// UI settings
	ui: {
		showPoseOverlay: boolean;
		showCoordinates: boolean;
		mirrorVideo: boolean;
		show_info_card: boolean;
	};
}

// Default configuration
const defaultConfig: AppConfig = {
	debug: {
		enabled:
			browser &&
			(localStorage.getItem('debug') === 'true' ||
				new URLSearchParams(window.location.search).has('debug')),
		ik: false,
		bodyTracking: false,
		mediaPipe: false,
		mujoco: false
	},

	mediaPipe: {
		modelComplexity: 1,
		minDetectionConfidence: 0.5,
		minTrackingConfidence: 0.5,
		smoothLandmarks: true
	},

	g1Controller: {
		autoInitialize: true,
		kp: 150.0,
		kd: null,
		debug: false
	},

	ui: {
		showPoseOverlay: true,
		showCoordinates: false,
		mirrorVideo: true,
		show_info_card: false
	}
};

// Main config store
export const appConfig = writable<AppConfig>(defaultConfig);

// Convenience stores for commonly used settings
export const debugEnabled = writable<boolean>(defaultConfig.debug.enabled);
export const debugBodyTracking = writable<boolean>(defaultConfig.debug.bodyTracking);
export const debugIk = writable<boolean>(defaultConfig.debug.ik);
export const showInfoCard = writable<boolean>(defaultConfig.ui.show_info_card);

// Update convenience stores when main config changes
appConfig.subscribe((config) => {
	debugEnabled.set(config.debug.enabled);
	debugBodyTracking.set(config.debug.bodyTracking);
	debugIk.set(config.debug.ik);
	showInfoCard.set(config.ui.show_info_card);
});

/**
 * Enable/disable debug mode globally
 */
export function setDebugMode(enabled: boolean): void {
	if (browser) {
		if (enabled) {
			localStorage.setItem('debug', 'true');
		} else {
			localStorage.removeItem('debug');
		}
	}

	appConfig.update((config) => ({
		...config,
		debug: {
			...config.debug,
			enabled
		}
	}));
}

/**
 * Enable specific debug category
 */
export function setDebugCategory(category: keyof AppConfig['debug'], enabled: boolean): void {
	appConfig.update((config) => ({
		...config,
		debug: {
			...config.debug,
			[category]: enabled
		}
	}));
}

/**
 * Update MediaPipe settings
 */
export function updateMediaPipeConfig(settings: Partial<AppConfig['mediaPipe']>): void {
	appConfig.update((config) => ({
		...config,
		mediaPipe: {
			...config.mediaPipe,
			...settings
		}
	}));
}

/**
 * Update G1 controller settings
 */
export function updateG1Config(settings: Partial<AppConfig['g1Controller']>): void {
	appConfig.update((config) => ({
		...config,
		g1Controller: {
			...config.g1Controller,
			...settings
		}
	}));
}

/**
 * Update UI settings
 */
export function updateUIConfig(settings: Partial<AppConfig['ui']>): void {
	appConfig.update((config) => ({
		...config,
		ui: {
			...config.ui,
			...settings
		}
	}));
}

/**
 * Reset config to defaults
 */
export function resetConfig(): void {
	appConfig.set(defaultConfig);
	if (browser) {
		localStorage.removeItem('debug');
	}
}

/**
 * Save config to localStorage
 */
export function saveConfig(): void {
	if (browser) {
		appConfig.subscribe((config) => {
			localStorage.setItem('appConfig', JSON.stringify(config));
		})();
	}
}

/**
 * Load config from localStorage
 */
export function loadConfig(): void {
	if (browser) {
		const saved = localStorage.getItem('appConfig');
		if (saved) {
			try {
				const config = JSON.parse(saved);
				appConfig.set({ ...defaultConfig, ...config });
			} catch (error) {
				console.warn('Failed to load saved config:', error);
			}
		}
	}
}
